import * as d3 from 'd3';
import * as _ from 'lodash';

import { Crosshair } from './Crosshair';
import { Legend } from './Legend';
import { ResizableBase } from './ResizableBase';
import { IPane } from './IPane';
import { IChartArea } from './IChartArea';
import { IPlotArea } from './IPlotArea';
import { ISeriesGroup } from './ISeriesGroup';
import { Zoom } from './Zoom';
import { IHorizontalGridLines } from './IHorizontalGridLines';
import { HorizontalGridLines } from './HorizontalGridLines';
import { IHorizontalLines } from './IHorizontalLines';
import { HorizontalLines } from './HorizontalLines';
import { IndividualAxes } from './IndividualAxes';
import { StaticArea } from './StaticArea';
import { ChartArea } from './ChartArea';
import { PlotArea } from './PlotArea';
import { SeriesGroup } from './SeriesGroup';
import { IAreaConfiguration } from '../interfaces/IAreaConfiguration';
import { IPaneConfiguration } from '../interfaces/IPaneConfiguration';
import { TimeAxis, VerticalAxis } from './axes/index';
import { TimeScale } from './scales/index';
import { ILogger, LoggerFactory, RoundingHelper, SymbolScaleHelper } from '../../helpers/helpers';
import { ISeriesManager } from '../managers/ISeriesManager';
import { OwnAxes } from './ownAxes';
import { LineSeries } from './series';
import { mapStackedSeries } from './stack/stackHelper';
import { RectFormat } from '../charts/interfaces';
import { renderStackedLines } from './stack/renderLines';
import { renderStackedSymbols } from './stack/renderSymbols';
import { renderBars } from './stack/renderBars';
import { getOrdinalScale } from './series/getOrdinalScale';
import { renderStackedLabels } from './stack/renderLabels';
import { LineChartData } from '../../models/LineChartData';
import { filterLabels } from './series/filterLabels';
import { Format } from './stack/Format';
import { PaneTitle } from './PaneTitle';
import {
    lineChartLeftMargin,
    lineChartBottomMargin,
    lineChartRightMargin,
    lineChartTopMargin,
    defaultMargin
} from '../../constants';
import { IMargins } from '../../models';

const fmt = d3.format('.2f');
const visibleStates = [0, 2];
const highlightedStates = [2];

export class Pane extends ResizableBase implements IPane {
    private _chartArea: IChartArea;
    private _crosshair: Crosshair<Date>;
    private _data: any[];
    private _dispatch: any;
    private _editMode: boolean;
    private _height: number;
    private _horizontalGridLines: IHorizontalGridLines;
    private _horizontalLines: IHorizontalLines;
    private _individualAxes: IndividualAxes;
    private _individualScales: { [identifier: string]: d3.scale.Linear<number, number> };
    private _isInitialized = false;
    private _isLegendVisible: boolean;
    private _legend: Legend;
    private _logger: ILogger;
    private _margin = defaultMargin;
    private _paneTitle: PaneTitle;
    private _plotArea: IPlotArea;
    private _roundingHelper = new RoundingHelper();
    private _seriesGroup: ISeriesGroup;
    private _seriesManager: ISeriesManager;
    private _staticArea: StaticArea;
    private _width: number;
    private _xAxis: TimeAxis;
    private _yAxis: VerticalAxis;
    private _yMax: number;
    private _yMin: number;
    private _zoom: Zoom;
    private bottom = lineChartBottomMargin;
    private left = lineChartLeftMargin;
    private right = lineChartRightMargin;
    private top = lineChartTopMargin;

    constructor(
        container: d3.Selection<any>,
        seriesManager: ISeriesManager,
        width: number,
        height: number
    ) {
        super(container, 'pane');

        this._width = width;
        this._height = height;
        this._seriesManager = seriesManager;
        this._data = [];
        this._logger = LoggerFactory.getLogger('WideTech.Charting.Pane', false, 0);
        this._editMode = false;
        this.init(this.group(), width, height);
    }

    private init(group: d3.Selection<any>, width: number, height: number) {
        try {
            this._logger.debug('Creating new pane');

            this._chartArea = new ChartArea(group, width, height);

            const plotAreaWidth = Math.max(width - this.left - this.right, 0);
            const plotAreaHeight = Math.max(height - this.top - this.bottom, 0);

            this._plotArea = new PlotArea(this._chartArea.group(), plotAreaWidth, plotAreaHeight);
            this._plotArea.group()
                .attr({ 'transform': `translate(${this.left},${this.top})` });

            const clipPathId = this._plotArea.clipPathId();
            const stackedGroup = this._plotArea.group()
                .append('g')
                .classed('stacked-series', true)
                .style({ 'clip-path': 'url(#' + clipPathId + ')' });
            stackedGroup.append('g')
                .classed('stacked-bars', true);
            stackedGroup.append('g')
                .classed('stacked-lines', true);
            stackedGroup.append('g')
                .classed('stacked-symbols', true)
                .classed('symbols', true);
            stackedGroup.append('g')
                .classed('stacked-labels', true);

            this._seriesGroup = new SeriesGroup(this._plotArea.group(), clipPathId);
            this.initSeriesManager();

            this._legend = new Legend(this._chartArea.group());
            this._legend.translate(width, height / 2);

            //Specific to chart with axes
            this._xAxis = new TimeAxis(plotAreaWidth, new Date(2015, 1, 1), new Date(2015, 2, 1), 6, this._plotArea.group(), true);
            this._xAxis.translate(0, plotAreaHeight);
            this._yAxis = new VerticalAxis(plotAreaHeight, 0, 1, 6, this._plotArea.group());

            this._horizontalGridLines = new HorizontalGridLines(this._plotArea.group(), this._yAxis.scale(), plotAreaWidth, plotAreaHeight);
            this._horizontalGridLines.move('.series-group');
            this._horizontalGridLines.move('.stacked-series');
            this._horizontalLines = new HorizontalLines(this._plotArea.group(), this._yAxis.scale(), plotAreaWidth, plotAreaHeight);
            this._horizontalLines.move('.crosshair-group');
            this._individualAxes = new IndividualAxes(this._plotArea.group());
            this._staticArea = new StaticArea(this._plotArea.group(),
                this._xAxis.scale(),
                this._plotArea.clipPathId(),
                this._plotArea.width(),
                this._plotArea.height());
            this._crosshair = new Crosshair<Date>(plotAreaWidth, plotAreaHeight, this._plotArea.group());
            this._zoom = new Zoom(this._plotArea.group(), this._xAxis, this._yAxis);

            const titleGroup = this._chartArea.group()
                .append('g')
                .classed('pane-title', true);
            this._paneTitle = new PaneTitle(titleGroup);

            const dispatch = this._zoom.dispatch();

            this.resizeFunction()(this._width, this._height);
            this._dispatch = d3.dispatch('panezoom', 'panepan', 'panemousedown', 'paneunzoom', 'selection');

            dispatch.on('zoom', data => {
                this._yAxis.update(data.min, data.max);
                this._horizontalLines.scale(this._yAxis.scale());
                for (const identifier of Object.keys(this._individualScales)) {
                    const id = `axis${identifier}`;
                    const individualScale = this._individualScales[identifier];
                    this._horizontalLines.group()
                        .selectAll('line[data-axis=' + id + ']')
                        // double equal on purpose, the object stringifies numbers
                        // tslint:disable-next-line:triple-equals
                        .data(this._horizontalLines.lines().filter(l => l.axis === identifier))
                        .attr('y1', d => individualScale(d.value))
                        .attr('y2', d => individualScale(d.value));
                }
                this.dispatch().panezoom(data);
            });

            dispatch.on('pan', data => {
                this.dispatch().panepan(data);
            });

            dispatch.on('mousedown', data => {
                this.dispatch().panemousedown(data);
            });

            dispatch.on('unzoom', data => {
                this.updateRange(data);
                this._yAxis.update(data.min, data.max);
                this._horizontalLines.scale(this._yAxis.scale());
                this.updateSeriesDisplay(this._roundingHelper);
                this.dispatch().paneunzoom(data);
            });

            dispatch.on('selection', data => this.dispatch().selection(data));

            this._legend.on('hover', d => {
                this.highlightSerie(d ? d._identifier : undefined);
            });

            this._legend.on('click', d => {
                this.setSerieState(d ? d._identifier : undefined, true);
            });

            this._isInitialized = true;
        } catch (e) {
            this._logger.error(e);
        }
    }

    private initSeriesManager() {
        this._seriesManager.container(this._seriesGroup.group());
        this._seriesManager.postAdd(series => {
            if (!series.isNotDisplayedData) {
                this._crosshair.addSeries(series);
            }
        });
        this._seriesManager.postUpdate(series => {
            if (!series.isNotDisplayedData) {
                this._crosshair.updateSeries(series);
            }
        });
        this._seriesManager.postRemove(series => {
            if (!series.isNotDisplayedData) {
                this._crosshair.removeSeries(series);
            }
        });
    }

    private updateYAxis() {
        try {
            this._logger.info('Updating Y axis.');
            this.updateYAxisMinMax();
            this._horizontalGridLines.scale(this._yAxis.scale());
            this._horizontalLines.scale(this._yAxis.scale());
            this._horizontalGridLines.updateHorizontalGridlines();
            this.updateSeriesDisplay(this._roundingHelper);
        } catch (e) {
            this._logger.error(e);
        }
    }

    private updateYAxisMinMax() {
        const seriesMinMax = this.getSeriesMinMax();
        const linesMinMax = this.getLinesMinMax();
        const stackedMinMax = this.getStackedMinMax();
        const all = seriesMinMax
            .concat(...linesMinMax)
            .concat(...stackedMinMax)
            .filter(d => !isNaN(d));
        const localMin = all.length ? d3.min(all) : NaN;
        const localMax = all.length ? d3.max(all) : NaN;
        let min = this._yMin;
        const hasMin = min !== null && min !== undefined;
        let max = this._yMax;
        const hasMax = max !== null && max !== undefined;
        if (!hasMin) {
            min = localMin;
        }
        if (!hasMax) {
            max = localMax;
        }
        if (!hasMin || !hasMax) {
            const rounded = this._roundingHelper.round(min, max);
            min = rounded.min;
            max = rounded.max;
        }
        min = hasMin ? this._yMin : min;
        max = hasMax ? this._yMax : max;
        if (isNaN(min)) {
            min = 0;
        }
        if (isNaN(max)) {
            max = 1;
        }
        this._yAxis.update(min, max);
    }

    private getSeriesMinMax(): [number, number] {
        let min: number;
        let max: number;
        const common = this.series()
            .filter((s: any) => (!s.hasOwnAxis || !s.hasOwnAxis())
                && !s.preventScalingAxis
                && !s.isStacked());
        if (common.length) {
            min = d3.min(common.map(s => s.min()));
            max = d3.max(common.map(s => s.max()));
        } else {
            min = NaN;
            max = NaN;
        }
        return [min, max];
    }

    private getLinesMinMax(): [number, number] {
        let min: number;
        let max: number;
        const allLines = this._horizontalLines.lines();
        const lines = allLines.filter(l => !l.axis);
        if (lines && lines.length > 0) {
            min = d3.min(lines, d => d.value);
            max = d3.max(lines, d => d.value);
        } else {
            min = NaN;
            max = NaN;
        }
        return [min, max];
    }

    private getStackedMinMax(): [number, number] {
        let min: number;
        let max: number;
        const { stacked } = this.mapStackedSeries();
        if (stacked.length) {
            const c = [].concat(...stacked.map(r => r.values));
            const values = c.map(r => (r.originalValue < 0) ? r.y0 : r.y0 + r.y);
            min = d3.min(values);
            max = d3.max(values);
        } else {
            min = NaN;
            max = NaN;
        }
        return [min, max];
    }

    private updateSeriesDisplay(roundingHelper: RoundingHelper) {
        this._logger.debug('Updating series display.');
        const common = this.series()
            .filter((s: any) => (!s.hasOwnAxis || !s.hasOwnAxis())/* && s.isVisible()*/);
        common.forEach((s: any) => {
            try {
                s.updateScales(this._xAxis.scale(), this._yAxis.scale());
                if (s.classed) {
                    s.classed('own-axis', false);
                }
            } catch (e) {
                this._logger.error(e);
            }
        });
        this.updateOwnAxes(roundingHelper);
        this.updateStacked();
        this._logger.debug('Series display updated.');
    }

    private updateOwnAxes(roundingHelper: RoundingHelper) {
        const individualScales = {};
        const oa = this.series()
            .filter((s: any) => (s.hasOwnAxis && s.hasOwnAxis()));
        oa.forEach((s: any) => {
            s.classed('own-axis', true);
            let sMin = s.min();
            let sMax = s.max();
            const identifier = s.identifier();
            const id = `axis${identifier}`;
            const lines = this._horizontalLines.lines().filter(l => l.axis === identifier);
            if (lines.length) {
                const values = lines.map(l => l.value);
                if (!s.hasMin()) {
                    sMin = d3.min([sMin].concat(values));
                }
                if (!s.hasMax()) {
                    sMax = d3.max([sMax].concat(values));
                }
            }
            const rounded = roundingHelper.round(sMin, sMax);
            const scale = d3.scale.linear();
            sMin = s.hasMin() ? sMin : rounded.min;
            sMax = s.hasMax() ? sMax : rounded.max;
            scale.domain([sMin, sMax]);
            scale.range(this._yAxis.scale().range());
            s.updateScales(this._xAxis.scale(), scale);
            this._horizontalLines.group()
                .selectAll('line[data-axis=' + id + ']')
                .data(lines)
                .attr('y1', d => scale(d.value))
                .attr('y2', d => scale(d.value));
            individualScales[identifier] = scale;
        });
        this._individualScales = individualScales;
        const ownAxis = OwnAxes(this.series(), this._individualScales);

        const plotAreaWidth = this._plotArea.width();
        const plotAreaHeight = this._plotArea.height();

        this._individualAxes
            .ratio(this._ratio)
            .update(ownAxis, plotAreaHeight, plotAreaWidth);
    }

    private updateData(data: any[]) {
        const currentData = this._seriesManager.data(data);
        this._crosshair.update(currentData);
        this.updateYAxis();
    }

    private resizePlotArea(w: number, h: number) {
        this._logger.debug(`Resizing plot area: width: ${w}, height: ${h}`);
        const ratio = this._ratio || 1;
        const titleAdjust = this.adjustTopForTitle() * ratio;
        const margins = {
            left: this.left * ratio,
            right: this.right * ratio,
            top: this.top * ratio,
            bottom: this.bottom * ratio
        };
        const legendWidth = this._legend.width();

        const plotAreaWidth = Math.max(w - margins.left - margins.right - legendWidth, 0);
        const plotAreaHeight = Math.max(h - margins.top - margins.bottom - titleAdjust, 0);
        this._plotArea.resize(plotAreaWidth, plotAreaHeight, this._plotArea);

        const left = this.isTitleLeftAligned();
        this._chartArea.group()
            .select('.pane-title')
            .attr('transform', left ? `translate(${margins.left},0)` : `translate(${margins.left + plotAreaWidth / 2},0)`);
        this._plotArea.group()
            .attr({ 'transform': `translate(${margins.left},${margins.top + titleAdjust})` });

        this._xAxis
            .ratio(this._ratio)
            .resize(plotAreaWidth);
        this._xAxis.translate(0, plotAreaHeight);
        this._yAxis.resize(plotAreaHeight);
        const scale = this._xAxis.scale();
        this._crosshair.resize(plotAreaWidth, plotAreaHeight);
        this._crosshair.scale(scale);
        this._staticArea.resize(plotAreaWidth, plotAreaHeight);
        this._staticArea.scale(scale);
        this._individualAxes.resize(plotAreaWidth, plotAreaHeight);
        this._horizontalGridLines.resizeFunction()(plotAreaWidth, plotAreaHeight);
        this._horizontalLines.resizeFunction()(plotAreaWidth, plotAreaHeight);
        this.updateSeriesDisplay(this._roundingHelper);

        this._logger.debug('Plot area resized');
    }

    private adjustTopForTitle() {
        return (this.isTitleVisible() && !this.isTitleLeftAligned())
            ? 15 : 0;
    }

    private mapStackedSeries() {
        const filteredSeries = this.series()
            .filter(s => s.isStacked())
            .reverse() as LineSeries[];
        if (filteredSeries.filter(f => f.data()).length) {
            const dates = d3.map([].concat(...filteredSeries.map(s => s.data())), s => s.timeStamp.toISOString
                ? s.timeStamp.toISOString()
                : s.timeStamp)
                .keys();
            const stacked = mapStackedSeries(filteredSeries, dates);
            return { stacked, dates };
        } else {
            return {
                stacked: [], dates: []
            };
        }
    }

    private updateStacked() {
        try {
            const { stacked, dates } = this.mapStackedSeries();
            const yScale = this._yAxis.scale();
            const xScale = this._xAxis.scale();

            const scaleOrdinal = getOrdinalScale(
                dates,
                xScale.domain() as [Date, Date],
                xScale.range() as [number, number],
                .3
            );
            const width = scaleOrdinal.rangeBand();
            const io: Format[] = stacked.map(s => {
                const cfmt = s.s.crosshairFormat();
                return {
                    series: s.s,
                    data: s.values.map(v => {
                        const value = Math.abs(v.y);
                        const height = Math.max(yScale(v.y0) - yScale(value + v.y0), 0);
                        const y = yScale(v.y0) - height;
                        const r: RectFormat<LineChartData> = {
                            x: xScale(v.x),
                            y,
                            width: s.s.isBarsVisible() ? width : 0,
                            height,
                            color: s.s.color(),
                            item: {
                                value: v.originalValue,
                                formattedValue: cfmt ? cfmt(v.originalData) : fmt(v.originalValue),
                                label: '',
                                symbol: s.s.symbol(),
                                timeStamp: v.x,
                            },
                            stackHide: v.hide
                        };
                        return r;
                    })
                };
            });

            const symbolScale = SymbolScaleHelper.scale(xScale, yScale);
            const ratio = this._ratio || 1;
            renderBars(this._plotArea.group().select('.stacked-bars'), io);
            renderStackedLines(this._plotArea.group().select('.stacked-lines'), io);
            renderStackedSymbols(this._plotArea.group().select('.stacked-symbols'), io, symbolScale * ratio);
            const labels = io.map(d => {
                return (Object as any).assign({}, d, { data: filterLabels(d.data, xScale, ratio) });
            });
            renderStackedLabels(this._plotArea.group().select('.stacked-labels'), labels, ratio);
        } catch (e) {
            this._logger.error(e);
        }

    }

    resizeFunction(): (width: number, height: number) => void {
        const self = this;
        return (w, h) => {
            self._logger.info(`Resizing pane (width: ${w}, height:${h}).`);
            self._chartArea.resize(w, h, self._chartArea);
            self._width = w;
            self._height = h;
            self._legend.resize(w, h);
            self._legend.translate(w, h / 2);
            self.resizePlotArea(w, h);
            self._logger.info(`Pane resized.`);
        };
    }

    private _ratio: number = 1;
    ratio(): number;
    ratio(value: number): this;
    ratio(value?: number) {
        if (arguments.length) {
            this._ratio = value;
            this._legend.ratio(value);
            this._seriesManager.series()
                .filter(s => !!s.ratio)
                .forEach(s => s.ratio(value));
            return this;
        }
        return this._ratio;
    }

    resize(width: number, height: number, obj: Pane): void {
    }

    update(scale: TimeScale): void {
    }

    updateRange(data: { beginDate: any, endDate: any }) {
        const scale = this._xAxis.scale();
        const domain = scale.domain();
        this._xAxis.update(data.beginDate, data.endDate);
        this._crosshair.scale(scale);
        this._staticArea.scale(scale);
        this.series().forEach((d: any) => {
            d.changeXScale(scale);
        });
        this.updateStacked();
    }

    updateSeries(series) {
        try {
            if (this._isInitialized) {

                this._logger.info('Updating series.');
                this._seriesManager.updateSeries(series);
                this.updateYAxis();
                this._legend.series(this._seriesManager.series());
                this._logger.info('Series updated.');
            }
        } catch (e) {
            this._logger.error(e);
        }
    }

    addData(data: any[]) {
        if (data) {
            this._logger.info(`Adding ${data.length} data.`);
            this._data = this._data.concat(data);
            try {
                this._crosshair.update(data);
                this._crosshair.scale(this._xAxis.scale());
                this._staticArea.scale(this._xAxis.scale());
                this._logger.info(`Data added.`);
                this.updateData(this._data);
            } catch (e) {
                this._logger.error(e);
            }
        }
    }

    // merge of updateRange and addData
    updateRangeAndData(data: any[], range: { beginDate, endDate }) {
        if (data) {
            this._logger.info(`Adding ${data.length} data.`);
            this._data = data;
            try {
                const scale = this._xAxis.scale();
                this._xAxis.update(range.beginDate, range.endDate);
                this._crosshair.scale(scale);
                this._staticArea.scale(scale);
                this._crosshair.update(data);
                this.updateData(this._data);
            } catch (e) {
                this._logger.error(e);
            }
        }
    }

    isLegendVisible(value?: boolean): boolean {
        if (arguments.length && value !== this._isLegendVisible) {
            this._isLegendVisible = value;
            this._legend.isVisible(this._isLegendVisible);
            this.resizeFunction()(this._width, this._height);
        }
        return this._legend && this._isLegendVisible;
    }

    isResizedByLegend(value?: boolean): boolean {
        if (value || value === false) {
            this._legend.isResizing(value);
        }
        return this._legend.isResizing();
    }

    isXaxisVisible(value?: boolean): boolean {
        let result = value;
        if (value !== this._xAxis.isAxisVisible()) {
            result = this._xAxis.isAxisVisible(value);
            if (result) {
                this.bottom = 20 + this._margin;
            } else {
                this.bottom = this._margin;
            }
            this.resizePlotArea(this._width, this._height);
        }
        return result;
    }

    isYaxisVisible(value?: boolean): boolean {
        const previousLeft = this.left;
        if (value === false) {
            this.left = 0;

        } else if (value === true) {
            this.left = lineChartLeftMargin;
        }
        if (previousLeft !== this.left) {
            this._xAxis.isYAxisVisible(value);
            this.resizePlotArea(this._width, this._height);
        }
        return this._yAxis.isAxisVisible(value);
    }

    isHorizontalGridLinesVisible(value?: boolean): boolean {
        return this._horizontalGridLines.isVisible(value);
    }

    isHorizontalLinesVisible(value?: boolean): boolean {
        return this._horizontalLines.isVisible(value);
    }

    horizontalLines(value: any[]): void {
        this._horizontalLines.lines(value);
    }

    clearData() {
        _.remove(this._data, () => true);
    }

    dispatch() {
        return this._dispatch;
    }

    pan() {
        this._zoom.pan(this.group());
    }

    select() {
        this._zoom.select(this.group());
    }

    isZoomed() {
        return this._zoom.isZoomed();
    }

    resetZoom() {
        this._zoom.resetZoom(this.group());
    }

    resetSelection() {
        this._zoom.resetSelection(this.group());
    }

    xMin(value: Date) {
        if (value) {
            try {
                this._xAxis.min(value);
                this._staticArea.scale(this._xAxis.scale());
                this.updateSeriesDisplay(this._roundingHelper);
            } catch (e) {
                this._logger.error(e);
            }
        }
    }

    xMax(value: Date) {
        if (value) {
            try {
                this._xAxis.max(value);
                this._staticArea.scale(this._xAxis.scale());
                this.updateSeriesDisplay(this._roundingHelper);
            } catch (e) {
                this._logger.error(e);
            }
        }
    }

    xRange(value: [Date, Date]) {
        if (value) {
            try {
                this._xAxis.min(value[0]);
                this._xAxis.max(value[1]);
                this._staticArea.scale(this._xAxis.scale());
                this.updateSeriesDisplay(this._roundingHelper);
            } catch (e) {
                this._logger.error(e);
            }
        }
    }

    yMin(value: number) {
        this._yMin = value;
        this.updateYAxis();
    }

    yMax(value: number) {
        this._yMax = value;
        this.updateYAxis();
    }

    series() {
        return this._seriesManager.series();
    }

    areas(areas: IAreaConfiguration[]) {
        this._staticArea.updateAreas(areas);
    }

    areaClickCallback(value?: (d: IAreaConfiguration) => void) {
        this._staticArea.clickCallback(value);
    }

    brushCallback(value?: (d: [Date, Date]) => void) {
        this._staticArea.brushCallback(value);
    }

    brushEndCallback(value?: (d: [Date, Date]) => void) {
        this._staticArea.brushEndCalback(value);
    }

    isZoomEnabled(value?: boolean): boolean {
        if (arguments.length) {
            this._zoom.isEnabled(value);
        }
        return this._zoom.isEnabled();
    }

    highlight(values: any[], keyFunction: (d: any) => any, classed: string, color: string) {
        this.series()
            .filter(s => s.highlight)
            .forEach(s => s.highlight(values, keyFunction, classed, color));
    }

    setSerieState(serieIdentifier: string, clicked: boolean) {
        this.updateYAxis();
        const lines = this._horizontalLines.lines();
        this.series().forEach((s: any) => {
            if (serieIdentifier === s._identifier) {
                s.setSerieState(clicked);
            } else {
                if (!clicked) {
                    s.setSerieState(false, 0, true);
                }
            }
            const isHidden = s.getSerieState
                ? s.getSerieState() === 1
                : false;
            lines
                .filter(l => l.seriesId === s._identifier)
                .forEach(l => l.isHidden = isHidden);
        });
        this._horizontalLines.lines(lines);
        const stackedSeries = this._plotArea.group()
            .select('.stacked-series');
        stackedSeries
            .selectAll<Format>('.series-container')
            .classed('highlighted', d => highlightedStates.indexOf(d.series.getSerieState()) >= 0)
            .classed('hidden', d => visibleStates.indexOf(d.series.getSerieState()) < 0);
    }

    highlightSerie(serieIdentifier: string) {
        this.series()
            .forEach((s: any) => {
                if (serieIdentifier === s._identifier) {
                    if (s.getSerieState && s.getSerieState() === 0) {
                        s.highlightSerie();
                    }
                } else {
                    if (s.clearHighlightSerie) {
                        if (s.getSerieState && s.getSerieState() === 0) {
                            s.clearHighlightSerie();
                        }
                    }
                }
            });
        const stackedSeries = this._plotArea.group()
            .select('.stacked-series');
        stackedSeries
            .selectAll('.highlighted')
            .classed('highlighted', d => highlightedStates.indexOf(d.series.getSerieState()) >= 0);
        stackedSeries
            .selectAll('[data-identifier=id' + serieIdentifier + ']')
            .classed('highlighted', true);
    }

    editMode(value?: boolean): boolean {
        if (arguments.length) {
            this._editMode = value;
            this.isZoomEnabled(this.isZoomEnabled() && !value);
            this.group().classed('edit', this.editMode() && value);
        }
        return this._editMode;
    }

    editArea(value: IAreaConfiguration) {
        if (!!value) {
            this.editMode(true);
        } else {
            this.editMode(false);
        }
        this._staticArea.editedArea(value);
    }

    title(): string;
    title(value: string): this;
    title(value?: string): this | string {
        if (arguments.length) {
            this._paneTitle
                .text(value)
                .render();
            return this;
        }
        return this._paneTitle.text();
    }

    isTitleVisible(value?: boolean): boolean {
        if (arguments.length) {
            this._paneTitle.isVisible(value)
                .render();
            this.resizePlotArea(this._width, this._height);
        }
        return this._paneTitle.isVisible();
    }

    isTitleLeftAligned(value?: boolean): boolean {
        if (arguments.length) {
            this._paneTitle
                .isLeftAligned(value)
                .render();
            this.resizePlotArea(this._width, this._height);
        }
        return this._paneTitle.isLeftAligned();
    }

    titleClickCallback(value?: () => void): void {
        if (arguments.length) {
            this._paneTitle.textClickCallback(value)
                .render();
        }
    }

    titleAttr(value?: { [key: string]: any }): void {
        if (arguments.length) {
            this._paneTitle.attr(value)
                .render();
        }
    }

    renderTitle() {
        this._paneTitle.render();
    }

    updateConfig(configuration: IPaneConfiguration) {
        this.isXaxisVisible(configuration.isXaxisVisible);
        this.isYaxisVisible(configuration.isYaxisVisible);
        this.isHorizontalGridLinesVisible(!!configuration.isHorizontalGridLinesVisible);
        this.horizontalLines(configuration.horizontalLines);
        this.areas(configuration.areas);
        this.areaClickCallback(configuration.areaClickCallback);
        this.brushCallback(configuration.brushCallback);
        this.brushEndCallback(configuration.brushEndCallback);
        this.editArea(configuration.editedArea);
        this.editMode(configuration.editMode);

        this._paneTitle
            .text(configuration.title)
            .isVisible(configuration.isTitleVisible)
            .isLeftAligned(configuration.isTitleLeftAligned)
            .textClickCallback(configuration.titleClickCallback)
            .iconClickCallback(configuration.iconClickCallback)
            .attr(configuration.titleAttributes)
            .faIcon(configuration.titleFaIcon)
            .render();
        this.yMin(configuration.yMin);
        this.yMax(configuration.yMax);
        this.ticksCount(configuration.ticksCount);
        this.isZoomEnabled(!configuration.isZoomDisabled);
        if (configuration.margins) {
            this.margins(configuration.margins);
        }
        this.resizePlotArea(this._width, this._height);
        this.updateYAxis();
    }

    height(value?: number): number {
        if (arguments.length) {
            this._height = value;
        }
        return this._height;
    }

    ticksCount(value?: number): number {
        return this._yAxis.tickCount(value);
    }

    data(data: any[]): void {
        if (this._data !== data) {
            this.clearData();
        }
    }

    onCrosshair(callback) {
        this._crosshair.onCrosshair(callback);
    }

    updateCrosshair(date: Date) {
        this._crosshair.setDate(date);
    }

    fixLegend() {
        this._legend.reselectAndResize();
    }

    private margins(value?: IMargins): this {
        if (arguments.length) {
            this.left = value.marginLeft;
            this.right = value.marginRight;
            this.top = value.marginTop;
            this.bottom = value.marginBottom;
        }
        return this;

    }
}